/**
 * The app's icon set, transcribed from the design canvas.
 *
 * Every glyph is a stroked 24x24 outline: no fills, round caps and joins, `currentColor` so an
 * icon takes the ink of whatever it sits in. Stroke weight carries emphasis - see
 * `docs/design/visual-system.md` §3 for the weight scale and where each name is used.
 *
 * These are hand-carried shapes rather than an icon package: `DEPENDENCIES.md` governs new
 * dependencies, and ~20 glyphs do not justify one.
 */

export type IconName =
  | 'camera'
  | 'image'
  | 'photo'
  | 'file-text'
  | 'bell'
  | 'settings'
  | 'alert-triangle'
  | 'check-circle'
  | 'x-circle'
  | 'info-circle'
  | 'check'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-down'
  | 'arrow-up'
  | 'arrow-down'
  | 'trash'
  | 'plus'
  | 'search'
  | 'note'
  | 'frame'
  | 'sun'
  | 'steady'
  | 'rotate';

/**
 * One glyph as the shapes the canvas draws it with. Keeping rects and circles as themselves,
 * rather than flattening everything to arc paths, keeps these readable against the artboards
 * they came from.
 */
type Shape =
  | { readonly kind: 'path'; readonly d: string }
  | {
      readonly kind: 'rect';
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly rx: number;
    }
  | { readonly kind: 'circle'; readonly cx: number; readonly cy: number; readonly r: number };

const path = (d: string): Shape => ({ kind: 'path', d });
const rect = (x: number, y: number, w: number, h: number, rx: number): Shape => ({
  kind: 'rect',
  x,
  y,
  w,
  h,
  rx,
});
const circle = (cx: number, cy: number, r: number): Shape => ({ kind: 'circle', cx, cy, r });

const GLYPHS: Readonly<Record<IconName, readonly Shape[]>> = {
  camera: [rect(3, 7, 18, 13, 2), path('M8 7l1.5-3h5L16 7'), circle(12, 13.5, 3.2)],
  // The gallery picker's mark: a full square frame.
  image: [rect(3, 3, 18, 18, 2), circle(8.5, 8.5, 1.5), path('M21 15l-5-5L5 21')],
  // The photo-of-a-note mark used for thumbnails: a wider, shorter frame.
  photo: [rect(3, 5, 18, 14, 2), circle(9, 11, 2), path('M21 16l-4.5-4.5a2 2 0 0 0-2.8 0L7 18')],
  'file-text': [
    path('M7 3h8l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z'),
    path('M14 3v4h4M9 12h6M9 16h6M9 8h2'),
  ],
  bell: [path('M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9'), path('M13.7 21a2 2 0 0 1-3.4 0')],
  settings: [
    circle(12, 12, 3),
    path(
      'M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 0 1-4 0v-.09A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.04H3a2 2 0 0 1 0-4h.09A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1.04-1.56V3a2 2 0 0 1 4 0v.09A1.7 1.7 0 0 0 15 4.6a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.56 1.04H21a2 2 0 0 1 0 4h-.09A1.7 1.7 0 0 0 19.4 15z',
    ),
  ],
  'alert-triangle': [
    path('M12 9v4M12 17h.01'),
    path(
      'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
    ),
  ],
  'check-circle': [circle(12, 12, 9), path('M8.5 12.5l2.5 2.5 5-5')],
  'x-circle': [circle(12, 12, 9), path('M9.5 9.5l5 5m0-5l-5 5')],
  'info-circle': [circle(12, 12, 9), path('M12 8v5M12 16h.01')],
  check: [path('M20 6L9 17l-5-5')],
  'chevron-left': [path('M15 18l-6-6 6-6')],
  'chevron-right': [path('M9 18l6-6-6-6')],
  'chevron-down': [path('M6 9l6 6 6-6')],
  'arrow-up': [path('M12 19V5M5 12l7-7 7 7')],
  'arrow-down': [path('M12 5v14M5 12l7 7 7-7')],
  trash: [
    path(
      'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6',
    ),
  ],
  plus: [path('M12 5v14M5 12h14')],
  search: [circle(11, 11, 7), path('M21 21l-4.3-4.3')],
  note: [path('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'), path('M14 2v6h6')],
  // The four capture tips, in the order they are shown.
  frame: [path('M3 3h6M3 3v6M21 3h-6M21 3v6M3 21h6M3 21v-6M21 21h-6M21 21v-6')],
  sun: [
    circle(12, 12, 5),
    path(
      'M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
    ),
  ],
  steady: [
    path(
      'M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8',
    ),
  ],
  rotate: [
    path('M17 2.1l4 4-4 4'),
    path('M3 12.6v-2a4 4 0 0 1 4-4h14.4M7 21.9l-4-4 4-4'),
    path('M21 11.4v2a4 4 0 0 1-4 4H2.6'),
  ],
};

export interface IconProps {
  readonly name: IconName;
  /** Rendered size in px, matching the canvas. Defaults to the 20 px nav/button size. */
  readonly size?: number;
  /** Canvas stroke weights run 1.6 (large/decorative) to 2.6 (small badge glyphs). */
  readonly strokeWidth?: number;
  /**
   * Accessible name for an icon that is the only content of a control. Omit wherever adjacent
   * text already says the same thing - which is most places here - and the icon is hidden from
   * assistive tech instead, so a screen reader never hears the label twice.
   */
  readonly label?: string | undefined;
  /** `exactOptionalPropertyTypes` is on, and CSS-module lookups are `string | undefined`. */
  readonly className?: string | undefined;
}

export function Icon({ name, size = 20, strokeWidth = 2, label, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label === undefined ? undefined : 'img'}
      aria-hidden={label === undefined ? true : undefined}
      aria-label={label}
      focusable="false"
    >
      {GLYPHS[name].map((shape, index) =>
        shape.kind === 'path' ? (
          <path key={index} d={shape.d} />
        ) : shape.kind === 'rect' ? (
          <rect
            key={index}
            x={shape.x}
            y={shape.y}
            width={shape.w}
            height={shape.h}
            rx={shape.rx}
          />
        ) : (
          <circle key={index} cx={shape.cx} cy={shape.cy} r={shape.r} />
        ),
      )}
    </svg>
  );
}
